"use client";

import { AlertCircle } from "lucide-react";
import Link from "next/link";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { routes } from "@/utils/routes";
import Base404 from "./base-404";

/**
 * General 404 page for invalid URLs/routes that don't exist
 * Maintains WillPower Store's professional tone and Birmingham identity
 */
const General404: FC = () => {
  return (
    <Base404>
      {/* Error Icon */}
      <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="h-8 w-8 text-destructive" />
      </div>

      {/* Main Heading */}
      <h1 className="heading-display font-bold font-heading text-4xl sm:text-5xl lg:text-6xl">
        Page Not Found
      </h1>

      {/* Subheading */}
      <h2 className="heading-section mt-4 font-bold font-heading text-muted-foreground text-xl sm:text-2xl">
        We can't find the page you're looking for
      </h2>

      {/* Description */}
      <p className="mx-auto mt-6 max-w-2xl text-balance text-lg text-muted-foreground text-optimal sm:text-xl">
        The page you requested doesn't exist or may have been moved. Let us help
        you find what you need to continue your fitness journey.
      </p>

      {/* Primary Actions */}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
        <Button
          asChild
          className="shadow-lg shadow-primary/20 transition-all hover:shadow-primary/25 hover:shadow-xl"
          size="lg"
        >
          <Link href={routes.home}>Go Home</Link>
        </Button>
        <Button asChild size="lg" variant="outline">
          <Link href={routes.shop}>Browse Products</Link>
        </Button>
      </div>
    </Base404>
  );
};

export default General404;
